import { ActionIcon, Badge, Box, Button, Card, Divider, Grid, Group, LoadingOverlay, Modal, Paper, Stack, Switch, Text, useMantineTheme } from "@mantine/core";
import { notifications, showNotification } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import InputField, { InputFieldMasks, InputFieldTypes } from "../components/input_field";
import Table from "../components/table";
import { useApp } from "../contexts/app.context";
import { useLanguage } from "../contexts/language.context";
import { GET_ALL_AGENTS } from "../services/agents";
import { DELETE_CAMPAIGN, GET_ALL_CAMPAIGNS, GET_CAMPAIGN_TRIGGER_PREVIEW, SAVE_CAMPAIGN, UPDATE_CAMPAIGN } from "../services/campaigns";
import { GET_ALL_CONTACTS_LIST } from "../services/contacts";
import { GET_ALL_PHONES } from "../services/phones";
import { formatMoney, timezones } from "../utility/util";
import { useAuth } from "../contexts/auth.context";
import { ActionsRender } from "../components/actions_render";

export default function CampaignsPage() {
    const [campaigns, setCampaigns] = useState<any[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [lists, setLists] = useState<any[]>([]);
    const [agents, setAgents] = useState([]);
    const [variables, setVariables] = useState([]);
    const [triggerPreview, setTriggerPreview] = useState<any>({});
    const [loadingTriggerPreview, setLoadingTriggerPreview] = useState<any>({});
    const [phones, setPhones] = useState<any[]>([]);
    const [filters, setFilters] = useState<any>({
        status: "active",
    });

    const getNewCampaignPayload = () => ({
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        periods: [
            { start: "09:00", finish: "12:00", weekdays: [1, 2, 3, 4, 5] },
            { start: "14:00", finish: "18:00", weekdays: [1, 2, 3, 4, 5] },
        ]
    });

    const theme = useMantineTheme();
    const navigate = useNavigate();

    const { confirmDialog } = useApp();
    const { str } = useLanguage();
    const { userData } = useAuth();

    const loadPhones = () => {
        GET_ALL_PHONES()
            .then((res) => {
                setPhones(res)
            })
            .catch(err => {
                notifications.show({ title: str("CAMPAIGNS.ERROR_TITLE"), message: err.message, color: 'red' })
            })
    }
    const loadCampaigns = () => {
        setLoading(true)
        GET_ALL_CAMPAIGNS()
            .then(res => {
                setLoading(false)
                setCampaigns(res.filter(c => c.status !== -1));
            })
            .catch(err => {
                setLoading(false)
                notifications.show({ message: err.message, color: "red" })
            })
    }

    const loadAgents = () => {
        GET_ALL_AGENTS()
            .then(setAgents)
            .catch(err => {
                notifications.show({ message: err.message, color: 'red' })
            })
    }

    const handleSave = () => {
        const { totals, ...params } = { ...selectedCampaign };

        setLoadingSave(true)
        SAVE_CAMPAIGN({ ...params })
            .then((res) => {
                notifications.show({ message: str("CAMPAIGNS.ITEM_SAVED"), color: "green" })
                loadCampaigns();
                setSelectedCampaign(null)
                setLoadingSave(false);
            })
            .catch(err => {
                notifications.show({ message: err.message, color: "red" })
                setLoadingSave(false);
            })
    }

    const loadContactLists = () => {
        GET_ALL_CONTACTS_LIST()
            .then((items) => {
                setLists(items);
            })
            .catch((err) => { });
    };

    const loadTriggerPreview = (campaignId: string) => {
        setLoadingTriggerPreview((ld: any) => ({ ...ld, [campaignId]: true }));
        GET_CAMPAIGN_TRIGGER_PREVIEW(campaignId)
            .then((res: any) => {
                setTriggerPreview((tp: any) => ({ ...tp, [campaignId]: res }));
            })
            .catch(err => {
                showNotification({ message: err.message, color: 'red' })
            })
            .finally(() => {
                setLoadingTriggerPreview((ld: any) => ({ ...ld, [campaignId]: false }));
            })
    }

    const formatPreviewDate = (value: any) => {
        if (!value) return str("CAMPAIGNS.NONE");
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toLocaleString();
    }

    const isInboundCampaign = (selectedCampaign?.type ?? "outbound") === "inbound";

    useEffect(loadCampaigns, []);
    useEffect(loadPhones, []);
    useEffect(loadContactLists, []);
    useEffect(loadAgents, []);
    useEffect(() => {
        const vs = [];
        const agent = agents.find(a => a.id === selectedCampaign?.agent);
        if (agent) {
            [
                ...(agent?.data?.instructions ?? "").match(/(\{\{)(.*?)(\}\})/g) ?? [],
                ...(agent?.data?.initial_message ?? "").match(/(\{\{)(.*?)(\}\})/g) ?? [],
            ].forEach((ks) => {
                const k = ks.replace(/\{\{|\}\}/g, "");
                if (!vs.includes(k)) vs.push(k);
            })
            setVariables(vs.filter(k => ![
                "contact_name",
                "contact_phone",
                "contact_email",
                "CurrentTime",
                "Contact.Name",
                "Contact.FirstName",
                "Contact.Phone",
                "Contact.Email",
            ].includes(k)))
        }
    }, [selectedCampaign?.agent, agents]);

    const filteredCampaigns = campaigns
        .filter(c => (
            filters.status === "all" ||
            (filters.status === "active" && c.status === 1) ||
            (filters.status === "inactive" && c.status !== 1)
        ));

    return <div style={{ position: 'relative' }}>
        {<LoadingOverlay visible={loading} />}
        <Paper mb="xl" p="md">
            <Group justify="flex-end">
                <InputField
                    name="status"
                    fieldType={InputFieldTypes.SELECT}
                    value={filters.status}
                    onChange={({ status }) => setFilters(f => ({ ...f, status }))}
                    options={[
                        { label: str("ALL"), value: "all" },
                        { label: str("ACTIVE"), value: "active" },
                        { label: str("INACTIVE"), value: "inactive" },
                    ]}
                />
                <Button
                    leftSection={<FaPlus />}
                    onClick={() => setSelectedCampaign(getNewCampaignPayload())}
                >
                    {str("CAMPAIGNS.NEW")}
                </Button>
            </Group>
        </Paper>

        <Grid gutter="md">

            {filteredCampaigns.length === 0 && !loading && (
                <Grid.Col span={12}>
                    <Paper p="xl" shadow="xs" withBorder>
                        <Stack align="center" gap="sm">
                            <Text fw={700} size="xl">{str("CAMPAIGNS.TITLE")}</Text>
                            <Text c="dimmed" ta="center">{str("CAMPAIGNS.EMPTY_STATE")}</Text>
                            <Button leftSection={<FaPlus />} onClick={() => setSelectedCampaign(getNewCampaignPayload())}>
                                {str("CAMPAIGNS.CREATE_FIRST")}
                            </Button>
                        </Stack>
                    </Paper>
                </Grid.Col>
            )}

            {filteredCampaigns.map(campaign => {
                const totals = campaign?.totals ?? {};
                const schedulePreview = triggerPreview[campaign.id];

                return (
                    <Grid.Col key={campaign.id} span={{ base: 12, xl: 12 }}>
                        <Paper shadow="xs" className="overflow-hidden" p="sm" withBorder>
                            <Stack gap="md">
                                <Group justify="space-between" align="flex-start" wrap="wrap">
                                    <Group align="flex-start" gap="sm">
                                        <Switch
                                            checked={campaign.status === 1}
                                            onChange={() => {
                                                const status = campaign.status === 1 ? 0 : 1;
                                                confirmDialog({
                                                    text: campaign.status === 1
                                                        ? str("CAMPAIGNS.STOP_CONFIRM")
                                                        : str("CAMPAIGNS.START_CONFIRM")
                                                }, ({ confirmed }) => {
                                                    if (confirmed) {
                                                        setCampaigns(c => c.map(it => it.id === campaign.id ? { ...it, status } : it))
                                                        UPDATE_CAMPAIGN(campaign.id, { status })
                                                            .then(() => { })
                                                            .catch(() => { })
                                                    }
                                                })
                                            }}
                                        />
                                        <Stack gap={2}>
                                            <Group gap="xs" wrap="wrap">
                                                <Text fw={800} size="lg">{campaign.title}</Text>
                                                <Badge color={campaign.status === 1 ? "green" : "gray"} variant="light">
                                                    {campaign.status === 1 ? str("ACTIVE") : str("INACTIVE")}
                                                </Badge>
                                                <Badge variant="light">{campaign.type === "inbound" ? str("CALLS.INBOUND") : str("CALLS.OUTBOUND")}</Badge>
                                            </Group>
                                            <Text size="xs" c="dimmed">
                                                {campaign?.phone?.number ?? str("CAMPAIGNS.NO_PHONE_ASSIGNED")}
                                            </Text>
                                        </Stack>
                                    </Group>
                                    <ActionsRender
                                        actions={[
                                            { title: str("CALLS.SEE"), onClick: () => { navigate(`/calls?campaign=${campaign.id}`) } },
                                            (userData?.user?.isAdmin && {
                                                title: schedulePreview ? str("CAMPAIGNS.REFRESH_TRIGGER_PREVIEW") : str("CAMPAIGNS.TRIGGER_PREVIEW"),
                                                onClick: () => { loadTriggerPreview(campaign.id) }
                                            }),
                                            {
                                                title: str("EDIT"), onClick: () => {
                                                    setSelectedCampaign({
                                                        ...campaign,
                                                        agent: campaign.agent?.id,
                                                        lists: (campaign.lists ?? []).map((l: any) => l.id),
                                                    })
                                                }
                                            },
                                            {
                                                title: str("CLONE"), onClick: () => {
                                                    setSelectedCampaign({
                                                        ...campaign,
                                                        _id: undefined,
                                                        lists: (campaign.lists ?? []).map((l: any) => l.id),
                                                    })
                                                }
                                            },
                                            {
                                                title: str("DELETE"), onClick: () => {
                                                    confirmDialog({ text: str("CAMPAIGNS.DELETE_CONFIRM") }, ({ confirmed }) => {
                                                        if (confirmed) {
                                                            setCampaigns(c => c.filter(it => it.id !== campaign.id))
                                                            DELETE_CAMPAIGN(campaign.id)
                                                                .then(() => { })
                                                                .catch(() => { })
                                                        }
                                                    })
                                                }
                                            },
                                        ].filter(Boolean)}
                                    />
                                </Group>

                                <Grid gutter="sm">
                                    <Grid.Col span={{ base: 12, md: 12 }}>
                                        <Card withBorder radius="md" p="sm" h="100%">
                                            <Stack gap="xs">
                                                <Group justify="space-between" align="center">
                                                    <Text fw={700} size="sm">{str("CAMPAIGNS.CONFIGURATION")}</Text>
                                                    <Badge variant="light" color="blue">{campaign?.timezone ?? str("CAMPAIGNS.NO_TIMEZONE")}</Badge>
                                                </Group>
                                                <Grid gutter="xs">
                                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                                        <Text size="xs" c="dimmed">{str("AGENTS.TITLE")}</Text>
                                                        <Text fw={600}>{campaign?.agent?.title ?? str("CAMPAIGNS.NO_AGENT")}</Text>
                                                    </Grid.Col>
                                                    {(campaign?.type ?? "outbound") === "outbound" && <>
                                                        <Grid.Col span={{ base: 12, sm: 6 }}>
                                                            <Text size="xs" c="dimmed">{str("LISTS")}</Text>
                                                            <Text fw={600}>{(campaign?.lists ?? []).map((l: any) => l.title).join(", ") || str("CAMPAIGNS.NO_LISTS")}</Text>
                                                        </Grid.Col>
                                                        <Grid.Col span={{ base: 12, sm: 6 }}>
                                                            <Text size="xs" c="dimmed">{str("CAMPAIGNS.ATTEMPTS_DELAY")}</Text>
                                                            <Text fw={600}>{campaign?.period_tries ?? 120} min</Text>
                                                        </Grid.Col>
                                                        <Grid.Col span={{ base: 12, sm: 6 }}>
                                                            <Text size="xs" c="dimmed">{str("CAMPAIGNS.MAX_DAILY_ATTEMPTS")}</Text>
                                                            <Text fw={600}>{campaign?.max_tries ?? 0}</Text>
                                                        </Grid.Col>
                                                        <Grid.Col span={{ base: 12, sm: 6 }}>
                                                            <Text size="xs" c="dimmed">{str("CAMPAIGNS.DAILY_BUDGET")}</Text>
                                                            <Text fw={600}>{formatMoney(campaign?.daily_limit ?? 0)}</Text>
                                                        </Grid.Col>
                                                    </>}
                                                </Grid>

                                                {(campaign?.type ?? "outbound") === "outbound" && <>
                                                    <Divider />

                                                    <Box>
                                                        <Text size="sm" fw={700} mb={6}>{str("PERIODS")}</Text>
                                                        <Group gap="xs" wrap="wrap">
                                                            {(campaign?.periods ?? []).length === 0 && <Text size="sm" c="dimmed">{str("CAMPAIGNS.NO_PERIODS_CONFIGURED")}</Text>}
                                                            {(campaign?.periods ?? []).slice(0, 3).map((period: any, index: number) => (
                                                                <Badge key={`${campaign.id}-period-${index}`} variant="light" color="gray">
                                                                    {period?.start ?? "--:--"} - {period?.finish ?? "--:--"}
                                                                </Badge>
                                                            ))}
                                                            {(campaign?.periods ?? []).length > 3 && <Badge variant="outline">+{(campaign?.periods ?? []).length - 3}</Badge>}
                                                        </Group>
                                                    </Box>
                                                </>}
                                            </Stack>
                                        </Card>
                                    </Grid.Col>

                                    <Grid.Col span={{ base: 12, md: 12 }}>
                                        <Stack gap="sm">
                                            <Card withBorder radius="md" p="sm">
                                                <Group justify="space-between" mb="xs">
                                                    <Text fw={700} size="sm">{str("CAMPAIGNS.PERFORMANCE")}</Text>
                                                    <Text size="xs" c="dimmed">{str("CAMPAIGNS.TOTALS_SOURCE")}</Text>
                                                </Group>
                                                <Grid gutter={3}>
                                                    {[
                                                        { title: str("CALLS.DIALS"), value: totals?.dials ?? 0 },
                                                        { title: str("CALLS.STATUS.PICKUP"), value: totals?.pickups ?? 0 },
                                                        { title: str("CALLS.STATUS.VOICEMAIL"), value: totals?.voicemails ?? 0 },
                                                        { title: str("CALLS.STATUS.FAILED"), value: totals?.failed ?? 0 },
                                                        { title: str("CALLS.STATUS.BUSY"), value: totals?.busy ?? 0 },
                                                        { title: str("CALLS.STATUS.NO-ANSWER"), value: totals?.no_answer ?? 0 },
                                                        { title: str("RESULTS"), value: totals?.outcomes ?? 0 },
                                                        { title: str("SPENT"), value: totals?.spent },
                                                    ].map(metric => (
                                                        <Grid.Col span={2} key={metric.title}>
                                                            <Paper withBorder p="xs" radius="md" style={{ background: theme.colors.gray[0] }}>
                                                                <Text ta="center" size="md" fw={800}>{metric.value}</Text>
                                                                <Text ta="center" size="xs" c="dimmed">{metric.title}</Text>
                                                            </Paper>
                                                        </Grid.Col>
                                                    ))}
                                                    <Grid.Col span={4}>
                                                        <Paper withBorder p="xs" radius="md" style={{ background: theme.colors.gray[0] }}>
                                                            <Text ta="center" size="md" fw={800}>${Number(totals?.cac ?? 0).toFixed(2)}</Text>
                                                            <Text ta="center" size="xs" c="dimmed">{str("COST_PER_RESULT")}</Text>
                                                        </Paper>
                                                    </Grid.Col>
                                                </Grid>
                                            </Card>

                                            {userData?.user?.isAdmin && schedulePreview && (
                                                <Card withBorder radius="md" p="sm">
                                                    <Group justify="space-between" align="flex-start">
                                                        <Stack gap={0}>
                                                            <Text fw={700} size="sm">{str("CAMPAIGNS.TRIGGER_PREVIEW")}</Text>
                                                            <Text size="xs" c="dimmed">{str("CAMPAIGNS.TRIGGER_PREVIEW_SUBTITLE")}</Text>
                                                        </Stack>
                                                        {loadingTriggerPreview[campaign.id] && <Text size="xs" c="dimmed">{str("CAMPAIGNS.REFRESHING")}</Text>}
                                                    </Group>

                                                    <Stack gap="xs" mt="sm">
                                                        <Group justify="space-between">
                                                            <Badge color={schedulePreview?.canTriggerNow ? "green" : "orange"} variant="light">
                                                                {schedulePreview?.canTriggerNow ? str("CAMPAIGNS.CAN_TRIGGER_NOW") : str("CAMPAIGNS.WAITING_FOR_SLOT")}
                                                            </Badge>
                                                            <Text size="xs" c="dimmed">{formatPreviewDate(schedulePreview?.nextEstimatedTriggerAt)}</Text>
                                                        </Group>

                                                        <Paper withBorder p="xs" radius="md">
                                                            <Text size="xs" fw={700} mb={6}>{str("CAMPAIGNS.NEXT_ELIGIBLE_CONTACT")}</Text>
                                                            <Text size="sm">
                                                                {schedulePreview?.nextContact
                                                                    ? (schedulePreview.nextContact.name ?? schedulePreview.nextContact.title ?? schedulePreview.nextContact.phone ?? JSON.stringify(schedulePreview.nextContact))
                                                                    : str("CAMPAIGNS.NONE")}
                                                            </Text>
                                                        </Paper>

                                                        <Stack gap="xs">
                                                            {(schedulePreview?.schedulerChecks ?? []).map((check: any, index: number) => (
                                                                <Paper key={`${campaign.id}-check-${index}`} withBorder p="xs" radius="md">
                                                                    <Group justify="space-between" align="flex-start">
                                                                        <Stack gap={0}>
                                                                            <Text size="sm" fw={600}>{check?.title ?? check?.name ?? `${str("CAMPAIGNS.CHECK")} ${index + 1}`}</Text>
                                                                            <Text size="xs" c="dimmed">{check?.details ?? str("CAMPAIGNS.NO_DETAILS")}</Text>
                                                                        </Stack>
                                                                        <Badge color={check?.passed ? "green" : "red"} variant="light">
                                                                            {check?.passed ? str("CAMPAIGNS.PASSED") : str("CAMPAIGNS.FAILED")}
                                                                        </Badge>
                                                                    </Group>
                                                                </Paper>
                                                            ))}
                                                        </Stack>

                                                        <Paper withBorder p="sm" radius="md">
                                                            <Group justify="space-between">
                                                                <Text size="sm" fw={600}>{str("CAMPAIGNS.EVALUATED_CONTACTS")}</Text>
                                                                <Text size="xs" c="dimmed">{(schedulePreview?.contacts ?? []).length}</Text>
                                                            </Group>
                                                            <Stack gap="xs" mt="xs">
                                                                {(schedulePreview?.contacts ?? []).length === 0 && <Text size="sm" c="dimmed">{str("CAMPAIGNS.NO_CONTACTS_IN_PREVIEW")}</Text>}
                                                                {(schedulePreview?.contacts ?? []).map((contact: any, index: number) => {
                                                                    const contactPass = contact?.eligible ?? contact?.pass;
                                                                    const criteria = contact?.criteria ?? contact?.checks ?? [];
                                                                    return (
                                                                        <Paper key={`${campaign.id}-contact-${contact?.id ?? index}`} withBorder p="xs" radius="sm">
                                                                            <Group justify="space-between" align="flex-start">
                                                                                <Stack gap={0}>
                                                                                    <Text size="sm" fw={600}>{contact?.name ?? contact?.title ?? contact?.phone ?? `${str("CONTACT")} ${index + 1}`}</Text>
                                                                                    {contact?.phone && <Text size="xs" c="dimmed">{contact.phone}</Text>}
                                                                                    {criteria.length > 0 && (
                                                                                        <Text size="xs" c="dimmed">
                                                                                            {criteria.map((criterion: any, criterionIndex: number) => (
                                                                                                <p key={`${campaign.id}-criterion-${criterionIndex}`}>
                                                                                                    {criterion?.title ?? criterion?.name ?? `${str("CAMPAIGNS.CRITERION")} ${criterionIndex + 1}`}: {criterion?.passed ?? criterion?.pass ? str("CAMPAIGNS.PASS") : str("CAMPAIGNS.FAIL")}
                                                                                                    {criterion?.details ? ` - ${criterion.details}` : ""}
                                                                                                    {criterionIndex < criteria.length - 1 ? " · " : ""}
                                                                                                </p>
                                                                                            ))}
                                                                                        </Text>
                                                                                    )}
                                                                                </Stack>
                                                                                <Badge color={contactPass ? "green" : "red"} variant="light">
                                                                                    {contactPass ? str("CAMPAIGNS.PASS") : str("CAMPAIGNS.FAIL")}
                                                                                </Badge>
                                                                            </Group>
                                                                        </Paper>
                                                                    )
                                                                })}
                                                            </Stack>
                                                        </Paper>
                                                    </Stack>
                                                </Card>
                                            )}
                                        </Stack>
                                    </Grid.Col>
                                </Grid>
                            </Stack>
                        </Paper>
                    </Grid.Col>
                )
            })}
        </Grid>
        {/* columns={
                [
                {
                    title: str("OFF_ON"), style: { width: 50 }, key: "status", render: (item) => <Switch
                        checked={item.status === 1}
                        onChange={() => {
                            const status = item.status === 1 ? 0 : 1;
                            confirmDialog({
                                text: item.status === 1
                                    ? str("CAMPAIGNS.STOP_CONFIRM")
                                    : str("CAMPAIGNS.START_CONFIRM")
                            }, ({ confirmed }) => {
                                if (confirmed) {
                                    setCampaigns(c => c.map(it => it.id === item.id ? { ...it, status } : it))
                                    UPDATE_CAMPAIGN(item.id, { status })
                                        .then(() => { })
                                        .catch(() => { })
                                }
                            })
                        }}
                    />
                },
                { title: str("CAMPAIGNS.TITLE"), key: "title" },
                { title: str("TYPE"), key: "type", style: { minWidth: 80 }, render: (item) => item.type === "inbound" ? "Inbound" : "Outbound" },
                { title: str("AGENTS.TITLE"), key: "agent", style: { minWidth: 80 }, render: (item) => item?.agent?.title },
                { title: str("LISTS"), key: "lists", style: { minWidth: 80 }, render: (item) => (item?.lists ?? []).map((l: any) => l.title).join(",") },
                { title: str("PHONE"), key: "phone", render: item => item?.phone?.number },
                { title: str("CALLS.DIALS"), key: "dials", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => item.totals?.dials ?? 0 },
                { title: str("CALLS.STATUS.PICKUP"), key: "pickups", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => item.totals?.pickups ?? 0 },
                { title: str("CALLS.STATUS.VOICEMAIL"), key: "voicemails", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => item.totals?.voicemails ?? 0 },
                { title: str("CALLS.STATUS.FAILED"), key: "failed", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => item.totals?.failed ?? 0 },
                { title: str("CALLS.STATUS.BUSY"), key: "busy", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => item.totals?.busy ?? 0 },
                { title: str("CALLS.STATUS.NO-ANSWER"), key: "no_answer", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => item.totals?.no_answer ?? 0 },
                { title: str("RESULTS"), key: "outcomes", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => item.totals?.outcomes ?? 0 },
                { title: str("SPENT"), key: "spent", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => `${(item.totals?.spent ?? 0)}` },
                (userData?.plan?.plan_type !== "packages" && { title: str("COST_PER_RESULT"), key: "cac", itemStyle: { textAlign: 'center' }, style: { minWidth: 80 }, render: (item) => `$${(item.totals?.cac ?? 0).toFixed(2)}` }),
                {
                    title: str("OTHERS"), key: "dynamic", style: { minWidth: 200 }, render: (item) => {
                        return [
                            <Text size="xs">{str("CAMPAIGNS.MAX_DAILY_ATTEMPTS")}: {item.max_tries ?? 0}</Text>,
                            <Text size="xs">{str("CAMPAIGNS.ATTEMPTS_DELAY")}: {item.period_tries ?? 120} min</Text>,
                            <Text size="xs">{str("CAMPAIGNS.DAILY_BUDGET")}: {formatMoney(item.daily_limit ?? 10)}</Text>,
                        ];
                    }
                },
                {
                    title: str("DYNAMIC_VALUES"), key: "dynamic", style: { minWidth: 200 }, render: (item) => {
                        let vls = [];
                        for (let key in item.dynamic) {
                            vls.push(<Text size="xs">{key}: {item.dynamic[key]}</Text>)
                        }
                        return vls;
                    }
                },
            ].filter(nn => nn)
            } */}

        <Modal
            opened={!!selectedCampaign}
            onClose={() => setSelectedCampaign(null)}
            size="xl"
        >

            <InputField
                name="title"
                onChange={({ title }) => setSelectedCampaign((sp: any) => ({ ...sp, title }))}
                value={selectedCampaign?.title}
                title={str("CAMPAIGNS.CAMPAIGN_TITLE")}
            />

            <InputField
                name="type"
                mt="md"
                onChange={({ type }) => setSelectedCampaign((sp: any) => ({ ...sp, type, phone: undefined }))}
                value={selectedCampaign?.type ?? "outbound"}
                title={str("TYPE")}
                options={[
                    { label: str("CALLS.OUTBOUND"), value: 'outbound' },
                    { label: str("CALLS.INBOUND"), value: 'inbound' },
                ]}
                fieldType={InputFieldTypes.BOX}
            />
            <Group mt="md">
                <InputField
                    fieldType={InputFieldTypes.SELECT}
                    name="phone"
                    value={selectedCampaign?.phone?.id ?? selectedCampaign?.phone}
                    style={{ flex: 1 }}
                    title={str("PHONE")}
                    onChange={({ phone }) => setSelectedCampaign((sp: any) => ({ ...sp, phone, phone_id: null }))}
                    options={phones
                        .filter(p =>
                            p.type === "sip" ||
                            (
                                ((selectedCampaign?.type ?? "outbound") === "outbound" || ["new"].includes(p?.type)) &&
                                ["in-use", "success"].includes(p?.data?.twilio?.status) ||
                                p?.data?.telnyx?.status
                            )
                        )
                        .map((l) => ({ value: l.id, label: l.number }))}
                />

                <InputField
                    fieldType={InputFieldTypes.SELECT}
                    name="agent"
                    value={selectedCampaign?.agent}
                    style={{ flex: 1 }}
                    title={str("AGENTS.TITLE")}
                    onChange={({ agent }) => setSelectedCampaign((sp: any) => ({ ...sp, agent, agent_id: null }))}
                    options={agents.map((l) => ({ value: l.id, label: l.title }))}
                />

                {!isInboundCampaign && <InputField
                    fieldType={InputFieldTypes.SELECT}
                    name="lists"
                    value={(selectedCampaign?.lists ?? [])}
                    style={{ flex: 1 }}
                    title={str("CONTACTS.LISTS")}
                    multiple
                    onChange={({ lists }) => setSelectedCampaign((sp: any) => ({ ...sp, lists }))}
                    options={lists.filter(list => list.id !== "unsigned").map((l) => ({ value: l.id, label: l.title }))}
                />}
            </Group>
            {!isInboundCampaign && <Group mt="md">
                <InputField
                    name="max_tries"
                    value={selectedCampaign?.max_tries ?? 0}
                    fieldType={InputFieldTypes.NUMBER}
                    style={{ flex: 1 }}
                    title={str("CAMPAIGNS.MAX_DAILY_ATTEMPTS")}
                    description={str("CAMPAIGNS.MAX_DAILY_ATTEMPTS_SUBTITLE")}
                    onChange={({ max_tries }) => setSelectedCampaign((sp: any) => ({ ...sp, max_tries }))}
                />

                <InputField
                    name="period_tries"
                    value={selectedCampaign?.period_tries ?? 120}
                    fieldType={InputFieldTypes.NUMBER}
                    style={{ flex: 1 }}
                    title={str("CAMPAIGNS.ATTEMPTS_DELAY")}
                    description={str("CAMPAIGNS.ATTEMPTS_DELAY_SUBTITLE")}
                    error={(selectedCampaign?.period_tries ?? 120) < 30 ? str("CAMPAIGNS.MIN_30") : ""}
                    rightSection={<Text>{str("MINUTES_ABREV").toLocaleLowerCase()}</Text>}
                    onChange={({ period_tries }) => {
                        setSelectedCampaign((sp: any) => ({ ...sp, period_tries }))
                    }}
                />

                {
                    userData?.plan?.plan_type === "packages"
                        ? <InputField
                            name="dailyPackageLimit"
                            value={selectedCampaign?.dailyPackageLimit ?? 500}
                            style={{ flex: 1 }}
                            title={str("CAMPAIGNS.DAILY_BUDGET")}
                            description={str("CAMPAIGNS.DAILY_BUDGET_SUBTITLE")}
                            error={(selectedCampaign?.dailyPackageLimit ?? 500) < 100 ? str("CAMPAIGNS.MIN_100") : ""}
                            onChange={({ dailyPackageLimit }) => { setSelectedCampaign((sp: any) => sp ? ({ ...sp, dailyPackageLimit }) : sp) }}
                            mask={InputFieldMasks.MONEY}
                        />
                        : <InputField
                            name="daily_limit"
                            value={selectedCampaign?.daily_limit ?? 10}
                            style={{ flex: 1 }}
                            title={str("CAMPAIGNS.DAILY_BUDGET")}
                            description={str("CAMPAIGNS.DAILY_BUDGET_SUBTITLE")}
                            error={(selectedCampaign?.daily_limit ?? 10) < 10 ? str("CAMPAIGNS.MIN_10") : ""}
                            onChange={({ daily_limit }) => { setSelectedCampaign((sp: any) => sp ? ({ ...sp, daily_limit }) : sp) }}
                            mask={InputFieldMasks.MONEY}
                        />
                }
            </Group>}

            {
                (variables ?? []).map(v => (
                    <InputField
                        title={v}
                        value={{ ...selectedCampaign?.dynamic }[v]}
                        name="vl"
                        mt="md"
                        onChange={({ vl }) => setSelectedCampaign(nc => ({ ...nc, dynamic: { ...nc.dynamic, [v]: vl } }))}
                    />
                ))
            }

            {!isInboundCampaign && <div>
                <InputField
                    title={str("TIMEZONE")}
                    value={selectedCampaign?.timezone}
                    name="timezone"
                    mt="md"
                    fieldType={InputFieldTypes.SELECT}
                    onChange={({ timezone }) => setSelectedCampaign(nc => ({ ...nc, timezone }))}
                    options={timezones.reduce((pv, tz) => {
                        return pv.concat(tz.utc.map(item => ({ value: item, label: `${item} ${tz.text}` })))
                    }, [])}
                />
                <Group mt="md">
                    <Text fw="bold" style={{ flex: 1 }}>{str("PERIODS")}</Text>
                    <ActionIcon
                        onClick={() => {
                            setSelectedCampaign(sc => ({ ...sc, periods: [...(sc?.periods ?? []), {}] }))
                        }}
                    ><FaPlus /></ActionIcon>
                </Group>
                {(selectedCampaign?.periods ?? []).map((p, pi) => <>
                    <Group align="flex-end" mt="md">
                        <InputField
                            name="start"
                            title={str("START")}
                            fieldType={InputFieldTypes.TIME}
                            value={p?.start}
                            style={{ flex: 1 }}
                            onChange={({ start }) => {
                                setSelectedCampaign(sc => ({ ...sc, periods: sc.periods.map((p2, pj) => pi === pj ? { ...p2, start } : p2) }))
                            }}
                        />
                        <InputField
                            name="finish"
                            title={str("FINISH")}
                            fieldType={InputFieldTypes.TIME}
                            value={p?.finish}
                            style={{ flex: 1 }}
                            onChange={({ finish }) => {
                                setSelectedCampaign(sc => ({ ...sc, periods: sc.periods.map((p2, pj) => pi === pj ? { ...p2, finish } : p2) }))
                            }}
                        />
                        <ActionIcon size="lg" variant="outline" onClick={() => {
                            setSelectedCampaign(sc => ({ ...sc, periods: sc.periods.filter((p2, pj) => pi !== pj) }))
                        }}><FaTrash /></ActionIcon>
                    </Group>
                    <Group justify="center" mt="xs">
                        {Array(7).fill({}).map((wd, wdi) =>
                            <Button
                                onClick={() => {
                                    setSelectedCampaign(d => ({
                                        ...d, periods: d.periods.map((p2, pj) => pi === pj ? {
                                            ...p2,
                                            weekdays: (p2.weekdays || []).includes(wdi)
                                                ? (p2.weekdays || []).filter(it => it !== wdi)
                                                : [...(p2.weekdays || []), wdi]
                                        } : p2)
                                    }))
                                }}
                                variant={(p.weekdays || []).includes(wdi) ? 'filled' : 'outline'}>
                                {str(`WEEKDAYS.${wdi}`)[0]}
                            </Button>
                        )}
                    </Group>
                </>)}
            </div>}

            <Group justify="center" mt="md">
                <Button
                    disabled={
                        (selectedCampaign?.period_tries ?? 30) < 30 ||
                        (selectedCampaign?.daily_limit ?? 10) < 10
                    }
                    size="sm" onClick={handleSave} loading={loadingSave} color="green">{str("CAMPAIGNS.SAVE")}</Button>
            </Group>
            {/* <Text ta="center" size="xs" mt="md">Telephony costs billed separately per <a href="https://www.twilio.com/en-us/pricing" target="_blank">Twilio's pricing</a></Text> */}
        </Modal>
    </div>
}
